import React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ConditionDropdown } from "./ConditionDropdown";
import { ViewsFilterMenu } from "./ViewsFilterMenu";
import type { Project } from "@/utils/types";
import type { FilterState } from "../../ProjectList/types";
import { FilterCondition, FilterType } from "@/types/filterEnums";

interface IconStackProps {
  icons: React.ReactNode[];
  maxVisible?: number;
  className?: string;
}

export const IconStack: React.FC<IconStackProps> = ({
  icons,
  maxVisible = 3,
  className = "",
}) => {
  const visibleIcons = icons.slice(0, maxVisible);
  return (
    <div className={`flex items-center ${className}`}>
      {visibleIcons.map((icon, index) => (
        <div
          key={index}
          className="relative"
          style={{ marginLeft: index > 0 ? "-4px" : "0" }}
        >
          {icon}
        </div>
      ))}
    </div>
  );
};

type FilterValue = string | number;

export interface FilterChipProps {
  filterType: string;
  value?: FilterValue;
  values?: FilterValue[];
  condition?: string;
  displayText: string;
  icon: React.ReactNode;
  valueIcon?: React.ReactNode;
  valueIcons?: React.ReactNode[];
  onRemove: () => void;
  onRemoveValue?: (valueToRemove: FilterValue) => void;
  onConditionChange?: (newCondition: string) => void;
  isClickable?: boolean;
  filterTypeForMenu?: string;
  currentProject?: Project | null;
  isConsolidated?: boolean;
  filters?: FilterState;
  updateFilters?: (filters: Partial<FilterState>) => void;
}

export const FilterChip = React.forwardRef<HTMLDivElement, FilterChipProps>(
  (
    {
      filterType,
      value,
      values,
      condition = FilterCondition.IS,
      displayText,
      icon,
      valueIcon,
      valueIcons,
      onRemove,
      onRemoveValue,
      onConditionChange,
      isClickable = true,
      filterTypeForMenu,
      currentProject,
      isConsolidated = false,
      filters,
      updateFilters,
    },
    ref,
  ) => {
    const handleConditionChange = (newCondition: string) => {
      if (onConditionChange) {
        onConditionChange(newCondition);
      }
    };

    return (
      <div
        ref={ref}
        className="flex h-6 items-center bg-secondary-n-900 rounded-md text-xs"
      >
        <div className="flex items-center gap-1 px-2 py-1 whitespace-nowrap">
          {icon}
          <span className="text-secondary-n-400 text-2xs">{filterType}</span>
        </div>

        {filterType !== FilterType.SEARCH && (
          <>
            <div className="flex items-center px-2 py-1 border-l-2 border-secondary-n-1000 whitespace-nowrap">
              {onConditionChange ? (
                <ConditionDropdown
                  condition={condition}
                  onConditionChange={handleConditionChange}
                  isConsolidated={isConsolidated}
                  values={values}
                />
              ) : (
                <span className="text-secondary-n-400 text-2xs">
                  {condition}
                </span>
              )}
            </div>

            <div className="border-l-2 border-secondary-n-1000">
              {filterTypeForMenu &&
              currentProject &&
              filters &&
              updateFilters ? (
                <ViewsFilterMenu
                  filterType={filterTypeForMenu}
                  currentProject={currentProject}
                  filters={filters}
                  updateFilters={updateFilters}
                >
                  <button
                    type="button"
                    disabled={!isClickable}
                    className={cn(
                      "flex items-center gap-2 px-2 py-1 border-l-2 border-secondary-n-1000 whitespace-nowrap hover:bg-secondary-n-800 transition-colors",
                      isClickable && "cursor-pointer",
                    )}
                  >
                    {isConsolidated && valueIcons ? (
                      <>
                        <IconStack icons={valueIcons} />
                        <span className="text-white text-2xs">
                          {displayText}
                        </span>
                      </>
                    ) : (
                      <>
                        {valueIcon}
                        <span className="text-white text-2xs">
                          {displayText}
                        </span>
                      </>
                    )}
                  </button>
                </ViewsFilterMenu>
              ) : (
                <div className="flex items-center gap-2 px-2 py-1 border-l-2 border-secondary-n-1000 whitespace-nowrap">
                  {isConsolidated && valueIcons ? (
                    <>
                      <IconStack icons={valueIcons} />
                      <span className="text-white text-2xs">{displayText}</span>
                    </>
                  ) : (
                    <>
                      {valueIcon}
                      <span className="text-white text-2xs">{displayText}</span>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="flex items-center px-2 py-1.5 border-l-2 border-secondary-n-1000 whitespace-nowrap">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRemove();
            }}
            className="hover:text-secondary-n-50 text-secondary-n-600"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  },
);

FilterChip.displayName = "FilterChip";

interface FilterChipsProps {
  children?: React.ReactNode;
  className?: string;
}

const FilterChips: React.FC<FilterChipsProps> = ({
  children,
  className = "",
}) => {
  return <div className={`flex flex-wrap gap-1 ${className}`}>{children}</div>;
};

export default FilterChips;
